// Shared building blocks for every Accounting Phase 8 report page: a common
// filter bar (Financial Year | From Date | To Date | extra filters | Search
// | Export | Print - item 33), a print-only header shell reusing the exact
// print CSS mechanism Phase 5 already fixed for vouchers (item 36 - see
// index.css's `.report-screen-only` / `.report-print-only` / #report-print-
// area rules, added alongside the pre-existing voucher-print-* ones rather
// than reusing them, so report printing can never interfere with voucher
// printing or vice versa), and small formatting helpers every report needs
// (Dr/Cr-aware balance text - item 7).
import { useEffect, useState } from "react";
import { useAuth } from "../../../../../context/AuthContext";
import { api, formatErr } from "../../../../../lib/api";
import { Button } from "../../../../../components/ui/button";
import { Input } from "../../../../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../../components/ui/select";
import { Download, Printer } from "lucide-react";
import { toast } from "sonner";
import { formatCurrency, formatDateDMY } from "../LineGrid";
import { useAccountingPermissions } from "../shared";

export { formatCurrency, formatDateDMY };

/** Item 7: a running/closing balance must always carry real Dr/Cr context,
 * never a bare signed number. `side` is "debit" | "credit" as already
 * returned by every Phase 8 report endpoint. */
export function formatBalance(amount, side) {
  // `side` is optional - when the caller only has the raw signed number
  // (positive = debit side, same convention as every Phase 8 report
  // endpoint), the Dr/Cr suffix is derived from its sign instead.
  const resolvedSide = side || (Number(amount || 0) >= 0 ? "debit" : "credit");
  const suffix = resolvedSide === "credit" ? "Cr" : "Dr";
  return `${formatCurrency(Math.abs(Number(amount || 0)))} ${suffix}`;
}

export function useFinancialYears() {
  const [financialYears, setFinancialYears] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/society/accounting/engine/financial-years");
        setFinancialYears(Array.isArray(res.data) ? res.data : []);
      } catch {
        setFinancialYears([]);
      }
    })();
  }, []);
  return financialYears;
}

/**
 * Downloads a report's CSV export - the same GET the on-screen data came
 * from, with `format=csv` appended, so an export always reflects the active
 * filters (item 38), never the whole database.
 */
export async function downloadReportCsv(path, params, filename) {
  try {
    const res = await api.get(path, { params: { ...params, format: "csv" }, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  } catch (err) {
    toast.error(formatErr(err, "Could not export CSV"));
  }
}

/**
 * Common filter bar: Financial Year | From Date | To Date, plus whatever
 * report-specific selector (Ledger, Bank Account, Member, Vendor...) the
 * caller passes as `extra`, plus Search (optional), Export and Print
 * actions. Every report page wires this the same way so the UX stays
 * consistent across all ten (item 33).
 */
export function ReportFilterBar({
  financialYears, financialYearId, onFinancialYearChange,
  fromDate, toDate, onFromDateChange, onToDateChange,
  search, onSearchChange, searchPlaceholder = "Search...",
  extra, onExport, onPrint, busy = false,
}) {
  // Note: this bar does not carry its own report-screen-only class - the
  // calling report page wraps its ENTIRE on-screen body (filter bar +
  // table) in one `.report-screen-only` div, exactly like VoucherViewPage
  // already does for the voucher-screen-only / voucher-print-only pair.
  // PHASE 19: Export CSV and Print are both gated on accounting.print here,
  // in this one shared component, so every one of the ~10 report pages that
  // render this bar picks up the permission gate automatically rather than
  // needing the same check copy-pasted into each page. The backend enforces
  // the identical check independently (see every report's `if format ==
  // "csv":` branch), so hiding these buttons is a UX convenience only.
  const perms = useAccountingPermissions();
  const canPrint = perms.canPrint;
  return (
    <div className="flex flex-wrap items-end gap-3 rounded-sm border border-[#E5E7EB] bg-white p-4">
      {financialYears && (
        <div className="w-48 space-y-1">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Financial Year</div>
          <Select value={financialYearId || "__none__"} onValueChange={(v) => onFinancialYearChange(v === "__none__" ? "" : v)}>
            <SelectTrigger><SelectValue placeholder="Current" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Current</SelectItem>
              {financialYears.map((fy) => <SelectItem key={fy.id} value={fy.id}>{fy.code}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      {onFromDateChange && (
        <div className="w-40 space-y-1">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">From Date</div>
          <Input type="date" value={fromDate || ""} onChange={(e) => onFromDateChange(e.target.value)} />
        </div>
      )}
      {onToDateChange && (
        <div className="w-40 space-y-1">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">To Date</div>
          <Input type="date" value={toDate || ""} onChange={(e) => onToDateChange(e.target.value)} />
        </div>
      )}
      {extra}
      {onSearchChange && (
        <div className="w-56 space-y-1">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Search</div>
          <Input value={search || ""} onChange={(e) => onSearchChange(e.target.value)} placeholder={searchPlaceholder} />
        </div>
      )}
      <div className="ml-auto flex gap-2">
        {onExport && canPrint && <Button variant="outline" size="sm" disabled={busy} onClick={onExport}><Download className="mr-2 h-4 w-4" /> Export CSV</Button>}
        {onPrint && canPrint && <Button variant="outline" size="sm" disabled={busy} onClick={onPrint}><Printer className="mr-2 h-4 w-4" /> Print</Button>}
      </div>
    </div>
  );
}

/**
 * Print-only header (item 36): Society Name, Report Name, Financial Year /
 * Date Range, Generated Date - then whatever table markup the caller
 * passes as children. Rendered inside `.report-print-only` so it only ever
 * appears in the browser's print output, never on screen (see index.css).
 */
export function ReportPrintShell({ reportName, rangeLabel, children }) {
  const { user } = useAuth();
  return (
    <div id="report-print-area" className="report-print-document mx-auto max-w-[1000px] bg-white p-2 text-[11px] leading-snug text-black">
      <div className="text-center">
        <div className="text-lg font-extrabold uppercase tracking-wide">{user?.name || "Housing Society"}</div>
        <div className="mt-1 text-sm font-bold uppercase tracking-[0.14em]">{reportName}</div>
        {rangeLabel && <div className="text-[11px] text-black">{rangeLabel}</div>}
        <div className="text-[10px] text-black">Generated: {new Date().toLocaleString("en-IN")}</div>
      </div>
      <hr className="my-2 border-black" />
      {children}
    </div>
  );
}

/** Small reconciliation banner used by Trial Balance / Balance Sheet /
 * Member & Vendor control checks (items 4/25/27/30) - the difference is
 * NEVER hidden, only ever colored to draw attention when nonzero. */
export function BalanceCheckBanner({ label, difference, okLabel = "Balanced" }) {
  const balanced = Math.abs(Number(difference || 0)) < 0.01;
  return (
    <div className={`rounded-sm border p-3 text-sm font-semibold ${balanced ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-red-300 bg-red-50 text-red-700"}`}>
      {label}: {formatCurrency(Math.abs(Number(difference || 0)))} {balanced ? `(${okLabel})` : "— MISMATCH, do not treat this data as final"}
    </div>
  );
}
